import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/landing/Nav";
import { CONTACT_EMAIL, WHATSAPP_NUMBER } from "@/config";

const sections = [
  {
    title: "1. Titolare del trattamento",
    body: `Il titolare del trattamento dei dati personali è MPX Digital di Marco Elia Pontello (di seguito "il Titolare"), contattabile all'indirizzo email ${CONTACT_EMAIL} o tramite WhatsApp al numero +39 351 048 3229.`,
  },
  {
    title: "2. Dati personali raccolti",
    body: `Attraverso questo sito raccogliamo esclusivamente i dati che ci fornisci volontariamente tramite il modulo di contatto o la sezione "Preventivo Guidato": nome e cognome, indirizzo email, tipo di progetto richiesto, budget indicativo e il contenuto del messaggio. Non raccogliamo dati sensibili né dati di pagamento.`,
  },
  {
    title: "3. Finalità e base giuridica del trattamento",
    body: `I dati forniti vengono utilizzati esclusivamente per: rispondere alla tua richiesta di preventivo o informazioni, ricontattarti tramite email o WhatsApp, e gestire l'eventuale rapporto commerciale successivo. La base giuridica del trattamento è l'esecuzione di misure precontrattuali adottate su tua richiesta (art. 6, par. 1, lett. b GDPR) e il legittimo interesse del Titolare a gestire le richieste ricevute (art. 6, par. 1, lett. f GDPR).`,
  },
  {
    title: "4. Modalità di trattamento e conservazione",
    body: `I dati sono trattati con strumenti informatici, con misure di sicurezza adeguate a proteggerli da accessi non autorizzati. Le richieste di contatto vengono conservate per il tempo necessario a gestire la richiesta e, in caso di rapporto commerciale, per i termini previsti dagli obblighi di legge. Le richieste che non si trasformano in collaborazioni vengono eliminate entro 24 mesi.`,
  },
  {
    title: "5. Comunicazione a terzi",
    body: `I tuoi dati non vengono venduti, ceduti o comunicati a terzi per finalità di marketing. Possono essere trattati da fornitori di servizi tecnici (hosting, infrastruttura cloud, servizi email) che operano come responsabili del trattamento nel rispetto del GDPR.`,
  },
  {
    title: "6. Cookie e strumenti di analisi",
    body: `Questo sito non utilizza cookie di profilazione propri o di terze parti a fini pubblicitari. Possono essere presenti strumenti di analisi tecnica che raccolgono dati di navigazione in forma aggregata e anonimizzata al solo scopo di migliorare il funzionamento del sito. Non è richiesto alcun consenso preventivo per tali strumenti tecnici.`,
  },
  {
    title: "7. Link a servizi esterni",
    body: `Il sito contiene link a servizi esterni (ad esempio WhatsApp di Meta Platforms). Cliccando su questi link verrai indirizzato a piattaforme soggette alle rispettive informative privacy, che ti invitiamo a consultare.`,
  },
  {
    title: "8. I tuoi diritti",
    body: `Ai sensi degli articoli 15-22 del GDPR hai il diritto di: accedere ai tuoi dati, chiederne la rettifica o la cancellazione, limitarne o opporti al trattamento, richiedere la portabilità dei dati e revocare il consenso in qualsiasi momento. Per esercitare i tuoi diritti scrivi a ${CONTACT_EMAIL}. Hai inoltre il diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).`,
  },
  {
    title: "9. Modifiche a questa informativa",
    body: `Il Titolare può aggiornare questa informativa in qualsiasi momento. La versione aggiornata è sempre disponibile su questa pagina. Ultimo aggiornamento: ottobre 2026.`,
  },
];

export default function PrivacyPage() {
  return (
    <main className="min-h-screen bg-[#09090B] text-zinc-50 font-jakarta grain" data-testid="privacy-page">
      <header className="border-b border-white/8 bg-zinc-950/60 backdrop-blur-xl">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-logo-link">
            <Logo size={34} />
            <span className="font-syne font-bold text-lg">
              MPX<span className="text-cyan-400"> Digital</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-cyan-300 transition-colors"
            data-testid="privacy-back-link"
          >
            <ArrowLeft size={16} />
            Torna al sito
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-4xl px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">Documento Legale</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-4 text-zinc-400">
            Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 ("GDPR")
            e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
          </p>
        </motion.div>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <motion.section
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.04 }}
              data-testid={`privacy-section-${i + 1}`}
            >
              <h2 className="font-syne text-lg font-semibold text-cyan-300">{s.title}</h2>
              <p className="mt-3 text-sm sm:text-base text-zinc-400 leading-relaxed">{s.body}</p>
            </motion.section>
          ))}
        </div>

        <div className="mt-16 border-t border-white/8 pt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="text-xs text-zinc-500">
            © {new Date().getFullYear()} MPX Digital — mpxdigital.it
          </p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-zinc-500 hover:text-cyan-300 transition-colors"
            data-testid="privacy-whatsapp-link"
          >
            Domande sulla privacy? Scrivici su WhatsApp
          </a>
        </div>
      </article>
    </main>
  );
}
